import { FRAIS_CAT } from '../constants/accounting';

/**
 * Ce que l'entreprise a déjà versé à l'État — TVA, AVS, impôts — saisi dans
 * Dépenses sous « Payé à l'État ». Ce ne sont pas des dépenses : les compter
 * fausserait le bénéfice (la TVA reversée a été encaissée chez les clients,
 * l'AVS et les impôts sont déjà estimés sur le bénéfice).
 */

/**
 * @param {string} [type] catégorie d'une dépense
 * @returns {'tva'|'avs'|'impots'|null} ce que la dépense paie à l'État, sinon `null`
 */
export function paiementEtat(type) {
  return (FRAIS_CAT[type] && FRAIS_CAT[type].etat) || null;
}

/**
 * Les dépenses qui sont de vraies charges : sans les paiements à l'État.
 *
 * @param {object[]} [frais] dépenses
 * @returns {object[]} les dépenses, paiements à l'État exclus
 */
export function sansPaiementsEtat(frais) {
  return (frais || []).filter((x) => !paiementEtat(x && x.type));
}

/**
 * Le jour qui range un paiement à l'État dans sa période : celui choisi
 * (« Pour l'année »), sinon, pour la TVA versée dans les deux premiers mois
 * de l'année — le décompte du dernier trimestre ou semestre, dû 60 jours
 * après sa fin —, le 31 décembre de l'année d'avant ; sinon le jour du
 * paiement. La TVA de 2025 payée le 27 février 2026 comptait pour 2026 et
 * affichait « trop payé ».
 *
 * @param {object} x dépense ({type, date, dateCompta})
 * @returns {string} le jour, au format JJ.MM.AAAA
 */
export function jourDuPaiementEtat(x) {
  if (x?.dateCompta) return x.dateCompta;
  const m = String(x?.date || '').match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
  if (paiementEtat(x?.type) === 'tva' && m) {
    const mois = +m[2];
    const jour = +m[1];
    if (mois === 1 || mois === 2 || (mois === 3 && jour === 1)) return '31.12.' + (+m[3] - 1);
  }
  return x?.date || '';
}

/**
 * Le déjà-payé à l'État sur une période, par impôt.
 *
 * @param {object[]} [frais] dépenses
 * @param {(date: string) => boolean} dansLaPeriode vrai pour une date de la période
 * @returns {{tva: number, avs: number, impots: number, total: number}} les montants versés
 */
export function dejaPayeEtat(frais, dansLaPeriode) {
  const paye = { tva: 0, avs: 0, impots: 0 };
  (frais || []).forEach((x) => {
    const sorte = paiementEtat(x && x.type);
    if (sorte && dansLaPeriode(jourDuPaiementEtat(x))) paye[sorte] += +x.montant || 0;
  });
  const cts = (v) => Math.round(v * 100) / 100;
  // Deux paiements du même montant à la même caisse : souvent un doublon
  // (« chaque semestre » recopiait la TVA payée).
  // L'AVS et les impôts se paient souvent par acomptes égaux, chaque mois ou
  // trimestre : pour eux, seuls deux paiements à moins d'un mois d'écart sont
  // suspects. Un décompte de TVA a toujours son propre montant.
  const vus = new Map();
  const jours = (d) => {
    const m = String(d || '').match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
    return m ? Date.UTC(+m[3], +m[2] - 1, +m[1]) / 86400000 : NaN;
  };
  (frais || []).forEach((x) => {
    const sorte = paiementEtat(x && x.type);
    if (!sorte || !dansLaPeriode(jourDuPaiementEtat(x)) || !(+x.montant > 0)) return;
    const cle = x.type + '|' + cts(+x.montant).toFixed(2);
    const deja = vus.get(cle) || { type: x.type, montant: cts(+x.montant), n: 0, dates: [] };
    const j = jours(x.date);
    const proche =
      sorte === 'tva' || deja.dates.some((d) => !Number.isFinite(j) || Math.abs(d - j) <= 31);
    vus.set(cle, {
      ...deja,
      n: deja.n === 0 ? 1 : proche ? deja.n + 1 : deja.n,
      suspect: deja.suspect || (deja.n > 0 && proche),
      dates: [...deja.dates, j],
    });
  });
  return {
    tva: cts(paye.tva),
    avs: cts(paye.avs),
    impots: cts(paye.impots),
    total: cts(paye.tva + paye.avs + paye.impots),
    doublons: [...vus.values()]
      .filter((d) => d.suspect)
      .map(({ type, montant, n }) => ({ type, montant, n })),
  };
}
